// Library methods
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Link from "@mui/material/Link";
import SystemCard from "../SystemCard/SystemCard";
import JobRow, { getJobStatus } from "../JobRow/JobRow";
import ReportOutbox from "../ReportOutbox/ReportOutbox";
import WorklistToday from "../WorklistToday/WorklistToday";
import LastImport from "../LastImport/LastImport";
import EditPacServerModal from "../EditPacServerModal/EditPacServerModal";

// Utils
import {
  describeConnectionJob,
  describeDicomAddress,
} from "src/utils/emr/jobDescriptionHelper";
import {
  findPacServerJob,
  pacServerFormValuesFromServer,
} from "src/utils/emr/pacServerHelper";
import { PacServerJobKey } from "../PacServerJobsForm/PacServerJobsForm";

// Types
import {
  EMRCatalogDeviceType,
  EMRConnectionType,
  EMRHubConnectionSourceKind,
  EMRPacServerType,
} from "src/services/emr/types";

type PacServerCardType = {
  clinicId: string | undefined;
  hubId: string | undefined;
  pacServer: EMRPacServerType;
  device: EMRCatalogDeviceType | undefined;
  icon: ReactNode;
  listenPort: string;
  hubHasOtherWorklist: boolean;
  hubIsActive: boolean;
  canEdit: boolean;
  jobKeys?: Array<PacServerJobKey>;
};

const JOBS: Array<{
  key: PacServerJobKey;
  title: string;
  kinds: Array<EMRHubConnectionSourceKind>;
  offText: string;
}> = [
  {
    key: "worklist",
    title: "Worklist",
    kinds: ["Worklist"],
    offText: "The worklist is not read",
  },
  {
    key: "exams",
    title: "Query",
    kinds: ["DicomQueryRetrieve", "DicomListener"],
    offText: "No exams are brought in",
  },
  {
    key: "reports",
    title: "Reports",
    kinds: ["DicomStore"],
    offText: "RetinaLogik reports are not sent back",
  },
];

const PacServerCard = ({
  clinicId,
  hubId,
  pacServer,
  device,
  icon,
  listenPort,
  hubHasOtherWorklist,
  hubIsActive,
  canEdit,
  jobKeys = ["worklist", "exams", "reports"],
}: PacServerCardType) => {
  const { t } = useTranslation();
  const [openEditModal, setOpenEditModal] = useState(false);

  const formValues = pacServerFormValuesFromServer(
    pacServer,
    device,
    listenPort
  );
  const examsJob = findPacServerJob(pacServer, [
    "DicomQueryRetrieve",
    "DicomListener",
  ]);
  const reportsJob = findPacServerJob(pacServer, ["DicomStore"]);
  const worklistJob = findPacServerJob(pacServer, ["Worklist"]);
  const firstAddressJob = pacServer.jobs.find((job) => job.config?.host);
  const subtitle = [t("DICOM"), describeDicomAddress(firstAddressJob?.config)]
    .filter(Boolean)
    .join(" · ");

  const describeJob = (job: EMRConnectionType | undefined, offText: string) =>
    job?.enabled ? describeConnectionJob(job, t) : t(offText);

  return (
    <>
      <SystemCard
        testId={`pac-server-card-${pacServer.pacServerId}`}
        icon={icon}
        name={pacServer.name}
        subtitle={subtitle}
        onEdit={canEdit ? () => setOpenEditModal(true) : undefined}
      >
        {JOBS.filter(({ key }) => jobKeys.includes(key)).map(
          ({ key, title, kinds, offText }) => {
            const job = findPacServerJob(pacServer, kinds);
            const hasOwnAddress =
              Boolean(job) && !formValues[key].useServerAddress;

            return (
              <JobRow
                key={key}
                testId={`pac-server-job-${key}`}
                direction={key === "reports" ? "Out" : "In"}
                title={t(title)}
                description={describeJob(job, offText)}
                status={getJobStatus(job, hubIsActive)}
                badge={hasOwnAddress ? t("Own address") : undefined}
                action={
                  !job?.enabled && canEdit ? (
                    <>
                      {" · "}
                      <Link
                        component="button"
                        onClick={() => setOpenEditModal(true)}
                      >
                        {t("Turn on")}
                      </Link>
                    </>
                  ) : undefined
                }
              />
            );
          }
        )}
        {worklistJob?.enabled && jobKeys.includes("worklist") && (
          <WorklistToday
            clinicId={clinicId}
            connectionId={worklistJob.connectionId}
          />
        )}
        {examsJob && jobKeys.includes("exams") && (
          <LastImport lastImport={examsJob.lastImport} />
        )}
        {reportsJob && jobKeys.includes("reports") && (
          <ReportOutbox
            clinicId={clinicId}
            connectionId={reportsJob.connectionId}
          />
        )}
      </SystemCard>

      {openEditModal && (
        <EditPacServerModal
          open={openEditModal}
          clinicId={clinicId}
          hubId={hubId}
          pacServer={pacServer}
          device={device}
          listenPort={listenPort}
          hubHasOtherWorklist={hubHasOtherWorklist}
          onClose={() => setOpenEditModal(false)}
        />
      )}
    </>
  );
};

export default PacServerCard;
